/**
 * Maps chat generation / tool activity onto Losi poses.
 *
 * Non-tool loading → ambient drive (connect / drift / comet).
 * Tool markers → specialized `agent*` holds, keyed the same way as the loading label.
 * Face stays catalogue `neutre` — other expressions are not battle-tested here yet.
 */

import type { StateId } from '@/lib/losi-engine/states'

export type ChatAgentStatus =
  | 'idle'
  | 'thinking'
  | 'running'
  | 'tooling'
  | 'connecting'
  | 'searching'
  | 'streaming'
  | 'waiting'
  | 'reviewing'

export type ChatLosiMode = 'greeting' | 'generating' | 'message'

/** How the loader should interpret `state`. */
export type ChatLosiDrive = 'ambient' | 'hold'

export interface ChatLosiPose {
  state: StateId
  status: ChatAgentStatus
  drive: ChatLosiDrive
}

/** Neutral loading combo — ink/cream only (no hue-wheel swirl/spin). */
export const AMBIENT_LOADER_STATES: readonly StateId[] = [
  'connect',
  'drift',
  'comet'
]

/** Exact / prefix keys used by LoadingMessage tool markers → agent hold. */
const TOOL_HOLD_BY_KEY: Array<{ match: RegExp | string; state: StateId; status: ChatAgentStatus }> =
  [
    { match: /googlesearch|websearch|web_search|searchweb|serp|duckduck|bing|deepresearch|yahoofinance|getweather|youtubecaptions|fetchdata|webscraper/i, state: 'agentSearch', status: 'searching' },
    { match: /connectedservices|composio|oauth|integration|extension|webhook/i, state: 'agentConnect', status: 'connecting' },
    { match: /losispaces|spaces__|space_|vectorscratch|virtualfilesystem|knowledge|retriev|rag|pdf|document|notes|memory/i, state: 'agentReview', status: 'reviewing' },
    { match: /sandbox|computer|desktop|terminal|shell|python|execute|manim|imagegenerator|gptimage|flux|stablediffusion|videogenerator|contentengine|productcreation|code/i, state: 'agentRun', status: 'running' },
    { match: /calendar|schedule_activity|taskautomation|set_todos|update_todo|show_plan|cron|poll|sync/i, state: 'agentSync', status: 'waiting' },
    { match: /rename_chat|edit_canvas|send_email|send_sms/i, state: 'agentChat', status: 'streaming' },
    { match: /handoff/i, state: 'agentHandoff', status: 'tooling' },
    { match: /list_agents|create_agent|update_agent|improve_agent|sidecar|helper|workforce|nexus|staff|crew|delegate/i, state: 'agentTool', status: 'tooling' }
  ]

function normalizeToolKey(toolKey: string) {
  return toolKey.toLowerCase().replace(/[^a-z0-9]+/g, '')
}

function hold(state: StateId, status: ChatAgentStatus): ChatLosiPose {
  return { state, status, drive: 'hold' }
}

function ambient(status: ChatAgentStatus): ChatLosiPose {
  // Placeholder state — LoadingLosiMark / MessageLosiMark cycle AMBIENT_LOADER_STATES.
  return { state: 'drift', status, drive: 'ambient' }
}

function resolveToolHold(toolKey: string): ChatLosiPose | null {
  const raw = toolKey.trim()
  if (!raw) return null

  const compact = normalizeToolKey(raw)

  for (const entry of TOOL_HOLD_BY_KEY) {
    if (typeof entry.match === 'string') {
      const needle = normalizeToolKey(entry.match)
      if (compact.includes(needle) || raw.toLowerCase().includes(entry.match.toLowerCase())) {
        return hold(entry.state, entry.status)
      }
      continue
    }
    if (entry.match.test(raw) || entry.match.test(compact)) {
      return hold(entry.state, entry.status)
    }
  }

  // Unknown tool marker still gets a generic agent tool hold so the body leaves ambient.
  return hold('agentTool', 'tooling')
}

/**
 * Pick a Losi pose from the active tool / reasoning phase.
 * Pass the same display key the loading label uses (`toolName` like
 * `webScraper__googleSearch`), optionally also the raw `toolId`.
 */
export function resolveChatLosiPose(input: {
  mode?: ChatLosiMode
  isReasoning?: boolean
  toolKey?: string | null
  toolId?: string | null
  isGenerating?: boolean
}): ChatLosiPose {
  if (input.mode === 'greeting') {
    return hold('logo', 'idle')
  }

  const candidates = [input.toolKey, input.toolId]
    .map((value) => value?.trim() || null)
    .filter((value): value is string => Boolean(value))

  for (const key of candidates) {
    const toolPose = resolveToolHold(key)
    if (toolPose) return toolPose
  }

  if (input.isReasoning) {
    return ambient('thinking')
  }

  if (input.isGenerating) {
    return ambient('streaming')
  }

  return ambient('idle')
}

/** True when the loader should hold a specialized agent body for a tool marker. */
export function isToolHoldPose(pose: ChatLosiPose): boolean {
  return pose.drive === 'hold' && pose.state.startsWith('agent')
}
